import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { addBreadcrumb } from '@sentry/react';
import React, {
	memo,
	type ReactNode,
	type CSSProperties,
	type MouseEvent,
} from 'react';

import { isAndroid, isApp } from 'pkg/platform';
import { useCurrentRoute } from 'pkg/router';
import {
	useRouterState,
	useRouterDispatch,
	useCurrentRouterState,
} from 'pkg/router/hooks';
import {
	transformPath,
	isLocalPathOrUrl,
	isOsHandledUrl,
} from 'pkg/router/utils';
import withForwardedRef from 'pkg/withForwardedRef';

interface LinkProps {
	href?: string;
	to?: string;
	replace?: string;
	back?: boolean;
	params?: Record<string, string | number | boolean>;
	query?: Record<string, string | number | boolean>;
	onClick?: (
		event: MouseEvent<HTMLAnchorElement>,
		shouldNavigate: boolean
	) => void;
	onMouseEnter?: (event: MouseEvent<HTMLAnchorElement>) => void;
	children?: ReactNode;
	linkStyle?: boolean;
	resetHistory?: boolean;
	style?: CSSProperties;
	className?: string;
	name?: string;
	title?: string;
	target?: string;
	disabled?: boolean;
	stopPropagation?: boolean;
	forwardedRef?: React.Ref<HTMLAnchorElement>;
	'data-testid'?: string;
	testid?: string;
	[key: `data-${string}`]: string | undefined;
}

interface AnchorProps extends Omit<
	React.AnchorHTMLAttributes<HTMLAnchorElement>,
	'href' | 'onClick'
> {
	href: string | null;
	onClick: ((event: MouseEvent<HTMLAnchorElement>) => void) | null;
	disabled?: boolean;
	ref?: React.Ref<HTMLAnchorElement>;
	'data-route-name'?: string;
	'data-current-page'?: boolean;
	'data-current-tree'?: boolean;
	'data-testid'?: string;
}

const isOpenInNewTabEvent = (event: MouseEvent<HTMLAnchorElement>): boolean =>
	event.ctrlKey || event.shiftKey || event.metaKey || event.button === 1;

const findFirstTextChild = (children: ReactNode): string | null => {
	let foundText: string | null = null;

	React.Children.forEach(children, (child) => {
		if (foundText) return;

		if (typeof child === 'string' && child.trim()) {
			foundText = child;
		} else if (React.isValidElement(child)) {
			const childElement = child as React.ReactElement<{
				children?: ReactNode;
			}>;
			if (childElement.props.children) {
				foundText = findFirstTextChild(childElement.props.children);
			}
		}
	});

	return foundText;
};

const Link: React.FC<LinkProps> = ({
	href,
	to,
	replace,
	back,
	params = {},
	query = {},
	onClick,
	children,
	linkStyle = true,
	resetHistory = false,
	onMouseEnter,
	...props
}) => {
	const { pushState, popState, replaceState } = useRouterState();
	const [currentState] = useCurrentRouterState('/');
	const dispatch = useRouterDispatch();

	const currentRoute = useCurrentRoute();
	const routeName = to || replace || '';

	params = { ...params, ...query };

	const nextState = transformPath({ href, to, replace, params }, currentRoute);

	let nextLocation = nextState.href;

	const shouldNavigate = isLocalPathOrUrl(nextLocation);

	if (shouldNavigate) {
		nextLocation = nextLocation.replace(
			/^(https|capacitor?)\:(\/{2})app\.360player\.com/,
			''
		);
	}

	let inlineStyles = {
		...props.style,
	};

	if (!linkStyle) {
		inlineStyles.color = 'inherit';
	}

	const navigate = (event: MouseEvent<HTMLAnchorElement>): void => {
		onClick?.(event, shouldNavigate);

		if (isOpenInNewTabEvent(event)) return;

		if (props.stopPropagation) {
			event.stopPropagation();
		}

		if (shouldNavigate) {
			event.preventDefault();

			if (back === true) {
				popState();
			} else if (to || href) {
				pushState(nextLocation, nextState.route);

				if (resetHistory === true) {
					dispatch('RESET');
				}
			} else if (replace) {
				replaceState(nextLocation, nextState.route);

				if (resetHistory === true) {
					dispatch('RESET');
				}
			}
		}
	};

	const getProperties = () => {
		const isLocal = isLocalPathOrUrl(href ?? '');
		const { className, name, target, forwardedRef, disabled } = props;
		let { title } = props;

		const dataAttributes = Object.entries(props).filter(([key]) =>
			key.startsWith('data-')
		);

		let newProps: AnchorProps = {
			href: nextLocation,
			onClick: navigate,
			title,
			className,
			disabled,
			onMouseEnter,
			...Object.fromEntries(dataAttributes),
		};

		if (!title && name) {
			title = name;
		}

		if (target) {
			newProps.target = target;
		}

		if (routeName) {
			newProps['data-route-name'] = routeName;
		}

		const currentLocation = currentState.location;

		const currentWithoutSearch = currentLocation.split('?')[0];

		const nextWithoutSearch = nextLocation.split('?')[0];

		if (currentWithoutSearch === nextWithoutSearch) {
			newProps['aria-current'] = 'page';
			newProps['data-current-page'] = true;
		}

		const currentLocationWithoutSlash = currentLocation.substring(1);
		const nextLocationWithoutSlash = nextLocation.substring(1);

		if (
			currentLocationWithoutSlash.length > 0 &&
			nextLocationWithoutSlash.length > 0 &&
			currentLocationWithoutSlash.startsWith(nextLocationWithoutSlash)
		) {
			newProps['data-current-tree'] = true;
		}

		if (currentLocation === '/' && nextLocation === currentLocation) {
			newProps['data-current-tree'] = true;
		}

		if (!isLocal) {
			newProps.onClick = (event: MouseEvent<HTMLAnchorElement>) => {
				onClick?.(event, shouldNavigate);
			};

			// tel:/mailto:/sms:/webcal: links must keep default anchor behavior:
			// the WebView (Capacitor) and regular browsers hand them to the OS.
			// Setting target="_blank" would route them through Browser.open below,
			// which only supports http/https and silently fails in the native app.
			if (!isOsHandledUrl(href)) {
				newProps.rel = 'noreferrer noopener';
				newProps.target = '_blank';
			}
		} else {
			// Strip away domain from local urls
			if (nextLocation.match(/https?:/i)) {
				const nextUrl = new URL(nextLocation || '');
				const domainName = `${nextUrl.protocol}//${nextUrl.host}`;

				newProps.href = nextLocation.replace(domainName, '');
			}
		}

		if (isApp() && newProps.target === '_blank') {
			newProps.onClick = (event: MouseEvent<HTMLAnchorElement>) => {
				event.preventDefault();
				onClick?.(event, shouldNavigate);
				Browser.open({ url: newProps.href });
			};
		}

		// Open Webcal links with App Launcher in iOS
		if (isApp() && !isAndroid() && newProps.href?.includes('webcal://')) {
			newProps.onClick = async (event: MouseEvent<HTMLAnchorElement>) => {
				event.preventDefault();
				onClick?.(event, shouldNavigate);

				const { value: canOpenUrl } = await AppLauncher.canOpenUrl({
					url: newProps.href,
				});

				if (canOpenUrl) {
					await AppLauncher.openUrl({
						url: newProps.href,
					});
				}
			};
		}

		// Open mailto links with App Launcher in iOS
		if (isApp() && !isAndroid() && newProps.href?.includes('mailto:')) {
			newProps.onClick = async (event: MouseEvent<HTMLAnchorElement>) => {
				event.preventDefault();
				onClick?.(event, shouldNavigate);

				const { value: canOpenUrl } = await AppLauncher.canOpenUrl({
					url: newProps.href,
				});

				if (canOpenUrl) {
					await AppLauncher.openUrl({
						url: newProps.href,
					});
				}
			};
		}

		newProps.ref = forwardedRef;

		if (disabled) {
			newProps.href = null;
			newProps.onClick = null;
			newProps.target = null;
		}

		if (back === true) {
			newProps.href = '#';
		}

		if ('data-testid' in props || 'testid' in props) {
			newProps['data-testid'] = props['data-testid'] || props['testid'];
		} else {
			newProps['data-testid'] = routeName || name || 'router.link';
		}

		const existingOnClick = newProps.onClick;

		newProps.onClick = (e: MouseEvent<HTMLAnchorElement>) => {
			const target = e.target as HTMLAnchorElement;
			// Not sure if this is bullet proof but from some testing it seems to be working quite good.
			const elementText = findFirstTextChild(children);
			const datasetAttributes = target.dataset
				? JSON.stringify(target.dataset)
				: null;

			addBreadcrumb({
				category: 'ui.click',
				message: `Clicked on Link - ${elementText}`,
				level: 'info',
				data: {
					tagName: target.tagName,
					dataset: datasetAttributes,
					page: window.location.pathname,
					nextHref: newProps.href,
				},
			});

			// If an existing onClick function exists, call it
			if (typeof existingOnClick === 'function') {
				existingOnClick(e);
			}
		};

		return newProps;
	};

	return (
		<a {...getProperties()} style={inlineStyles}>
			{children}
		</a>
	);
};

export default withForwardedRef(memo(Link));
